import { ARABIC_FONTS, POPULAR_FONTS } from './designSystemConstants';

const METADATA_URL = 'https://fonts.google.com/metadata/fonts';
const loadedFonts = new Set<string>();
let cachedFamilies: string[] | null = null;
let fetchPromise: Promise<string[]> | null = null;

function parseMetadataPayload(text: string): string[] {
  const json = text.replace(/^\)\]\}'\n?/, '');
  const data = JSON.parse(json) as { familyMetadataList?: Array<{ family: string }> };
  return (data.familyMetadataList ?? []).map((item) => item.family).sort((a, b) => a.localeCompare(b));
}

export async function fetchGoogleFontFamilies(): Promise<string[]> {
  if (cachedFamilies) return cachedFamilies;
  if (!fetchPromise) {
    fetchPromise = fetch(METADATA_URL)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load Google Fonts catalog');
        return res.text();
      })
      .then(parseMetadataPayload)
      .then((families) => {
        cachedFamilies = families;
        return families;
      })
      .catch(() => {
        cachedFamilies = [...POPULAR_FONTS];
        return cachedFamilies;
      })
      .finally(() => {
        fetchPromise = null;
      });
  }
  return fetchPromise;
}

export function toGoogleFontsCssFamily(family: string): string {
  return encodeURIComponent(family).replace(/%20/g, '+');
}

export function loadGoogleFontPreview(family: string): void {
  if (!family || loadedFonts.has(family)) return;
  loadedFonts.add(family);

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${toGoogleFontsCssFamily(family)}:wght@400;600&display=swap`;
  document.head.appendChild(link);
}

export function filterFontFamilies(families: string[], query: string, limit = 80): string[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return families.slice(0, limit);

  const startsWith: string[] = [];
  const includes: string[] = [];

  for (const family of families) {
    const lower = family.toLowerCase();
    if (lower.startsWith(trimmed)) startsWith.push(family);
    else if (lower.includes(trimmed)) includes.push(family);
    if (startsWith.length + includes.length >= limit) break;
  }

  return [...startsWith, ...includes].slice(0, limit);
}

export function getDisplayedFontSections(
  families: string[],
  query: string,
  selected?: string,
): Array<{ id: 'popular' | 'arabic' | 'results'; label: string; fonts: string[] }> {
  const trimmed = query.trim();
  const catalog = families.length > 0 ? families : [...POPULAR_FONTS, ...ARABIC_FONTS];

  if (trimmed) {
    const filtered = filterFontFamilies(catalog, query);
    const fonts =
      selected && !filtered.includes(selected) ? [selected, ...filtered] : filtered;
    return fonts.length > 0 ? [{ id: 'results', label: 'Results', fonts }] : [];
  }

  const inCatalog = (font: string) => catalog.includes(font);
  const popular = POPULAR_FONTS.filter(inCatalog);
  const arabic = ARABIC_FONTS.filter(inCatalog);
  const extras =
    selected && !popular.includes(selected) && !arabic.includes(selected) ? [selected] : [];

  return [
    ...(extras.length > 0 ? [{ id: 'results' as const, label: 'Selected', fonts: extras }] : []),
    { id: 'popular', label: 'Popular', fonts: popular },
    { id: 'arabic', label: 'Arabic', fonts: arabic },
  ].filter((section) => section.fonts.length > 0);
}

export function getDisplayedFontOptions(
  families: string[],
  query: string,
  selected?: string,
): string[] {
  return getDisplayedFontSections(families, query, selected).flatMap((section) => section.fonts);
}
